/**
 * DayScheduleWidget - Time-based schedule view for today
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { WidgetWrapper } from './WidgetWrapper';
import { AdaptiveModal } from '../AdaptiveModal';
import type { DayScheduleWidgetConfig } from '../../types/widgets';
import { useTodos } from '../../hooks/useTodos';
import { eventsAPI } from '../../services/api';
import { TaskStatus } from '../../types';
import type { Todo, Event } from '../../types';
import { parseLocalTimestamp } from '../../utils/dateUtils';

interface DayScheduleWidgetProps {
  config: DayScheduleWidgetConfig;
  widgetId: string;
  windowId: string;
}

export function DayScheduleWidget({ config, widgetId, windowId }: DayScheduleWidgetProps) {
  const [expanded, setExpanded] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const { todos } = useTodos();
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const selectedDateStr = selectedDate.toISOString().split('T')[0];
  const isToday = selectedDateStr === todayStr;

  const goToPrevDay = useCallback(() => {
    setSelectedDate(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() - 1);
      return d;
    });
  }, []);

  const goToNextDay = useCallback(() => {
    setSelectedDate(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + 1);
      return d;
    });
  }, []);

  const goToToday = useCallback(() => {
    setSelectedDate(new Date());
  }, []);

  // Fetch selected day's events
  const { data: dayEvents = [] } = useQuery({
    queryKey: ['events', 'date', selectedDateStr],
    queryFn: () => eventsAPI.getByDate(selectedDateStr),
  });

  // Filter to selected day's tasks
  const dayTasks = todos.filter((todo) => {
    const todoDate = todo.deadline;
    if (!todoDate) return false;
    return todoDate.startsWith(selectedDateStr);
  });

  const currentHourRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const startHour = config.startHour ?? 0;
  const endHour = config.endHour ?? 24;
  const hours = Array.from({ length: endHour - startHour }, (_, i) => startHour + i);
  const currentHour = today.getHours();

  // Scroll to current hour on mount (within widget only, not the page)
  useEffect(() => {
    if (currentHourRef.current && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = currentHourRef.current.offsetTop - scrollContainerRef.current.offsetTop;
    }
  }, []);

  const formatHour = (hour: number) => {
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h = hour % 12 || 12;
    return `${h} ${ampm}`;
  };

  // Get events for a specific hour
  const getEventsForHour = (hour: number) => {
    return dayEvents.filter(event => {
      if (!event.start_time) return false;
      const eventHour = parseLocalTimestamp(event.start_time).getHours();
      return eventHour === hour;
    });
  };

  const handleTaskClick = (todo: Todo) => {
    window.dispatchEvent(new CustomEvent('openTaskFromWidget', { detail: { todo } }));
  };

  const handleEventClick = (event: Event) => {
    // If event is linked to a todo, open the todo
    if (event.todo_id) {
      const linkedTodo = todos.find(t => t.id === event.todo_id);
      if (linkedTodo) {
        window.dispatchEvent(new CustomEvent('openTaskFromWidget', { detail: { todo: linkedTodo } }));
        return;
      }
    }
    // For standalone events, dispatch schedule event to open EventModal
    window.dispatchEvent(new CustomEvent('openEventFromWidget', { detail: { event } }));
  };

  const scheduleContent = (
      <div ref={scrollContainerRef} className="p-2 h-full overflow-auto">
        {/* Date navigation */}
        <div className="mb-2 flex items-center justify-between">
          <button
            onClick={goToPrevDay}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 touch-manipulation"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-300 font-medium">
              {selectedDate.toLocaleDateString('default', { weekday: 'short', month: 'short', day: 'numeric' })}
            </span>
            {!isToday && (
              <button
                onClick={goToToday}
                className="text-xs px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800/50 touch-manipulation"
              >
                Today
              </button>
            )}
          </div>
          <button
            onClick={goToNextDay}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 touch-manipulation"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Time slots */}
        <div className="relative">
          {hours.map((hour) => {
            const hourEvents = getEventsForHour(hour);
            const minuteOffset = today.getMinutes() / 60;
            return (
              <div key={hour} ref={hour === currentHour ? currentHourRef : undefined} className="relative flex items-start border-t border-gray-100 dark:border-gray-700">
                <span className="w-12 text-xs text-gray-400 py-2 flex-shrink-0">
                  {formatHour(hour)}
                </span>
                <div className="flex-1 min-h-[40px] py-1">
                  {hourEvents.map(event => (
                    <div
                      key={event.id}
                      onClick={() => handleEventClick(event)}
                      className="text-xs p-1.5 mb-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 rounded border border-indigo-200 dark:border-indigo-700 cursor-pointer hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors"
                      title={event.description || event.title}
                    >
                      <div className="font-medium truncate">{event.title}</div>
                      {event.end_time && (
                        <div className="text-indigo-600 dark:text-indigo-400">
                          {parseLocalTimestamp(event.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          {' - '}
                          {parseLocalTimestamp(event.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {/* Current time indicator line */}
                {isToday && hour === currentHour && (
                  <div
                    className="absolute left-0 right-0 flex items-center pointer-events-none z-10"
                    style={{ top: `${minuteOffset * 100}%` }}
                  >
                    <div className="flex-1 h-px bg-red-500" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Unscheduled tasks */}
        {dayTasks.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-xs font-medium text-gray-500 mb-2">{isToday ? "Today's" : selectedDate.toLocaleDateString('default', { month: 'short', day: 'numeric' })} Tasks</h4>
            <ul className="space-y-1">
              {dayTasks.map((todo) => (
                <li
                  key={todo.id}
                  onClick={() => handleTaskClick(todo)}
                  className="flex items-center gap-2 p-2 rounded bg-gray-50 dark:bg-gray-700 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <span
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      todo.status === TaskStatus.COMPLETED
                        ? 'bg-green-500'
                        : todo.status === TaskStatus.IN_PROGRESS
                        ? 'bg-blue-500'
                        : 'bg-gray-400'
                    }`}
                  />
                  <span className={`truncate ${todo.status === TaskStatus.COMPLETED ? 'line-through text-gray-400' : ''}`}>
                    {todo.title}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
  );

  return (
    <>
      <WidgetWrapper title="Schedule" widgetId={widgetId} windowId={windowId} onHeaderClick={() => setExpanded(true)}>
        {scheduleContent}
      </WidgetWrapper>
      {expanded && (
        <AdaptiveModal isOpen onClose={() => setExpanded(false)} title="Schedule" size="xl">
          <div className="max-h-[80vh] overflow-auto">{scheduleContent}</div>
        </AdaptiveModal>
      )}
    </>
  );
}
